import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { catchError, of } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { ProfileNotificationsActions } from '../../notification-center/store/actions/actions-type.action';
import { PlayerNotification } from '../../profile/models/currentPlayer.interface';
import { TournamentState } from '../../tournament/models/enums/tournaments.enum';
import { TournamentGeneralActions } from '../../tournament/store/actions/actions-type.action';
import { endpoints } from '../../utils/endpoints';
import {
    WalletPaymentAccount,
    WithdrawalRequest,
} from '../../wallet/models/wallet.interface';
import { WalletActions } from '../../wallet/store/actions/actions-type.action';
import { AppState } from '../store/appState';

export enum MainHubMethods {
    'NotificationAdded' = 'NotificationAdded',
    'BalanceUpdated' = 'BalanceUpdated',
    'WithdrawalRequestAdded' = 'WithdrawalRequestAdded',
    'WithdrawalRequestApproved' = 'WithdrawalRequestApproved',
    'WithdrawalRequestDeclined' = 'WithdrawalRequestDeclined',
    'TournamentStateUpdated' = 'TournamentStateUpdated',
}

@Injectable(    {
    providedIn: 'root',
})
export class ProfileMainHubService {
    private authService = inject(AuthService);
    private store = inject<Store<AppState>>(Store);

    private hubConnection: signalR.HubConnection;

    connect() {
        this.startConnection();
    }

    private startConnection() {
        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(endpoints.notifications.children.getMainHubUrl(), {
                accessTokenFactory: () => this.authService.accessToken,
            })
            .configureLogging(signalR.LogLevel.Error)
            .withAutomaticReconnect()
            .build();

        of(this.hubConnection.start())
            .pipe(
                catchError((err) =>
                    of(console.error('Error while starting connection: ' + err))
                )
            )
            .subscribe(() => {
                console.log('Connection started');

                this.defineMainHubEvents();
            });
    }

    private defineMainHubEvents() {
        this.hubConnection.on(
            MainHubMethods.NotificationAdded,
            (notification: PlayerNotification) =>
                this.notificationAdded(notification)
        );

        this.hubConnection.on(
            MainHubMethods.BalanceUpdated,
            (account: WalletPaymentAccount) => this.onBalanceUpdated(account)
        );

        this.hubConnection.on(
            MainHubMethods.WithdrawalRequestAdded,
            (request: WithdrawalRequest) =>
                this.onWithdrawalRequestAdded(request)
        );

        this.hubConnection.on(
            MainHubMethods.WithdrawalRequestApproved,
            (requestId: string) => this.onWithdrawalRequestApproved(requestId)
        );

        this.hubConnection.on(
            MainHubMethods.WithdrawalRequestDeclined,
            //TODO: create method for this event.
            (requestId: string) => this.onWithdrawalRequestApproved(requestId)
        );

        this.hubConnection.on(
            MainHubMethods.TournamentStateUpdated,
            (message: { tournamentId: string; state: TournamentState }) =>
                this.onTournamentStateUpdated(message)
        );
    }

    private notificationAdded(notification: PlayerNotification) {
        this.store.dispatch(
            ProfileNotificationsActions.postNewProfileNotification({
                payload: notification,
            })
        );
    }

    private onBalanceUpdated(account: WalletPaymentAccount) {
        this.store.dispatch(
            WalletActions.fetchProfilePaymentAccountsSuccess({
                payload: [account],
            })
        );
    }

    private onWithdrawalRequestAdded(request: WithdrawalRequest) {
        this.store.dispatch(
            WalletActions.signalRWithdrawalRequestAdded({
                payload: { request },
            })
        );
    }

    private onWithdrawalRequestApproved(requestId: string) {
        this.store.dispatch(
            WalletActions.signalRWithdrawalRequestApproved({
                payload: { requestId },
            })
        );
    }

    private onTournamentStateUpdated(message: {
        tournamentId: string;
        state: TournamentState;
    }) {
        this.store.dispatch(
            TournamentGeneralActions.signalRTournamentStateUpdated({
                payload: message,
            })
        );
    }
}
